import React, { useState } from "react";
import "../../../css/View/VariantRegistrations.css";
import { useNavigate, useParams } from "react-router-dom";

const groupRegistrations = (registrations) => {
  const grouped = { active: [], waitlist: [], cancelledBeforeConfirmation: [], cancelledAfterConfirmation: [], deleted: [] };

  registrations.forEach((r) => {
    if (r.status === 6) grouped.deleted.push(r);
    else if (r.status === 5) {
      if (r.confirmedAt == null) grouped.cancelledBeforeConfirmation.push(r);
      else grouped.cancelledAfterConfirmation.push(r);
    } else if (r.status === 2) grouped.waitlist.push(r);
    else if (r.status === 3 || r.status === 4 || !r.createdAt) grouped.active.push(r);
  });

  for (let key in grouped) {
    grouped[key].sort((a, b) => {
      if (key.includes("cancelled")) return new Date(b.cancelledAt || 0) - new Date(a.cancelledAt || 0);
      if (key === "deleted") return new Date(b.deletedAt || 0) - new Date(a.deletedAt || 0);
      return new Date(a.prereservationAt || 0) - new Date(b.prereservationAt || 0);
    });
  }

  return grouped;
};

const getTotalParticipants = (list) => list.reduce((sum, r) => {
  if (!r.createdAt) return sum + (r.pax || 0);
  return sum + (r.participants?.filter(p => p.status !== 4 && p.status !== 5).length || 0);
}, 0);

const RegistrationSection = ({ title, list, defaultExpanded = true, sectionRef = null, registrationBasePath = null }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const navigate = useNavigate();
  const { guid: tripGuid } = useParams();
  const totalPeople = getTotalParticipants(list);
  const EPS = 1e-9;
  const hidePeopleCount = ["Anulowane przed potwierdzeniem", "Anulowane po potwierdzeniu", "Usunięte rezerwacje"].includes(title);

  const openRegistration = (registration) => {
    const path = registrationBasePath
      ? `${registrationBasePath}/${registration.reservationGuid}`
      : `/trips/${tripGuid}/variants/${registration.variantGuid}/registrations/${registration.reservationGuid}`;
    navigate(path);
  };

  return (
    <div className="registration-group" ref={sectionRef}>
      <div className="registration-group-title" onClick={() => setExpanded(!expanded)}>
        <i className={`fa-solid fa-chevron-${expanded ? "up" : "down"} chevron-icon`} />
        <strong>{title} 📄{list.length}{!hidePeopleCount && ` · 👤${totalPeople}`}</strong>
      </div>

      {expanded && (
        <div className="registration-group-content">
          {list.length === 0 ? (
            <div className="registration-group-empty">Brak</div>
          ) : list.map((r) => {
            const isIncomplete = !r.createdAt;
            const isCancelled = r.status === 5;
            const isDeleted = r.status === 6;
            const statusClass = isDeleted ? "status-6" : isCancelled ? "status-5" : isIncomplete ? "status-null" : `status-${r.status ?? "undefined"}`;
            const mainParticipant = r.participants?.find(p => p.isPrimary);
            const mainName = isIncomplete ? r.createdByEmail ?? "Nieznany użytkownik" : mainParticipant?.preferredName ?? "Nieznana osoba";
            const activeParticipantsCount = r.participants?.filter(p => p.status !== 4 && p.status !== 5).length || 0;
            const iconCount = isIncomplete ? `(${r.pax}x👤)` : r.participants.length > 0 ? `(${activeParticipantsCount}x👤)` : "";
            const rawDisplayedBalance = ((r.totalTransfersAmount ?? 0) - (r.outgoingTransfersAmount ?? 0)) - (r.amountToPay ?? 0);
            const displayedBalance = Math.abs(rawDisplayedBalance) < EPS ? 0 : rawDisplayedBalance;
            const coordinatorNote = (r.coordinatorNotes || "").trim();
            const showCoordinatorNote = coordinatorNote?.length > 0;

            return (
              <div key={`reg-${r.reservationGuid}`} className="registration-entry new-format clickable" onClick={() => openRegistration(r)}>
                <div className="registration-line">
                  <span className={`reg-code ${statusClass}`}>{r.reservationCode}</span>
                  <div className="registration-line-content">
                    <div className="reg-main-name">
                      <span className="reg-name-text">{mainName}</span>
                      {r.userComment && <>📋</>}
                      {r.contractDeliveryMethod == 2 && <>🏚️</>}
                      {showCoordinatorNote && <div className="reg-note">{coordinatorNote}</div>}
                    </div>
                    <div className="reg-icons">
                      {[3, 4, 5].includes(r.status) && r.createdAt != null && r.totalTransfersAmount != null && r.amountToPay != null && (
                        <span className={`reg-saldo ${displayedBalance > 0 ? "saldo-positive" : displayedBalance < 0 ? "saldo-negative" : "saldo-zero"}`}>{displayedBalance.toFixed(2)} zł</span>
                      )}
                    </div>
                    {!hidePeopleCount && iconCount && <div className="reg-icons">{iconCount}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RegistrationsListView = ({ registrations, activeRef, registrationBasePath = null }) => {
  const grouped = groupRegistrations(registrations);

  return (
    <div className="registrations-groups-layout">
      <div className="registrations-groups-row registrations-groups-row--primary">
        <RegistrationSection title="Aktywne rejestracje" list={grouped.active} defaultExpanded={true} sectionRef={activeRef} registrationBasePath={registrationBasePath} />
        <RegistrationSection title="Lista rezerwowa" list={grouped.waitlist} defaultExpanded={true} registrationBasePath={registrationBasePath} />
        <RegistrationSection title="Anulowane po potwierdzeniu" list={grouped.cancelledAfterConfirmation} defaultExpanded={true} registrationBasePath={registrationBasePath} />
      </div>
      <div className="registrations-groups-row registrations-groups-row--secondary">
        <RegistrationSection title="Anulowane przed potwierdzeniem" list={grouped.cancelledBeforeConfirmation} defaultExpanded={false} registrationBasePath={registrationBasePath} />
        <RegistrationSection title="Usunięte rezerwacje" list={grouped.deleted} defaultExpanded={false} registrationBasePath={registrationBasePath} />
      </div>
    </div>
  );
};

export default RegistrationsListView;
